import {
  manualAuditStatus,
  scanAuthenticationType,
  scanFindingArchivalState,
  scanningType,
  ScanOrigin,
  scanStatus,
  standardNameAndLevel,
} from '../constants/scanning';
import { IAudits } from './audits.interface';
import { IMobileEvaluationUserFlows } from './mobile-manual-evaluation.interface';
import { IScopingRecommendedPage } from './scoping-page.interface';
import {
  $automatedUserFlowError,
  $brokenLinkData,
  $dlqError,
  $pageWithComponents,
  $rerunScan,
  $scan,
  $scanAutomatedOptions,
  $scanHeaders,
  $scanScope,
  $scanSearch,
  $scanMobile,
  $scanMobilePlatformName,
  $scanMobileDeviceType,
} from '../constants/scan';
import { $urlScan } from '../constants/url-scan';
import { IUser } from './user.interface';
import { IScanSchedule } from './scan-schedule.interface';
import { IGlobalError } from './error.interface';
import { ISecureWebAuthentication } from './scan-secuire-web-authentication.interface';
import { IFile } from './file.interface';
import { IScanViewport } from './scan-viewport.interface';
import { IDigitalProperty } from './digital-property.interface';
import { IUploads } from './uploads.interface';
import { AuditTypes } from '../constants/audit-types';
import { IScanTag } from './scan-tag.interface';
import { AccessibilityAuditToolNames } from '../constants/audit-tool';
import { IWorkspace } from './workspace.interface';
import { IDiscoveredScannableDocument } from './scannable-document.interface';
import { CrawlingStatus } from './crawling-status.interface';
import { ScopingCrawlStatus } from '../constants/scoping-crawl';
import { ApiQueryOption } from '../constants/api';
import { IdStringArray, IdString } from './id-string.interface';
import { UserFlowActionType } from '../constants/automated-user-flow';
import { IPage } from './page.interface';
import { ITestGuide } from './test-guide.interface';
import { IMatchComponent } from './match-component.interface';
import { EvaluationMediums, EvaluationStandards } from '../constants/manual-evaluation';
import { $scanOptions } from '../constants/scan-options';
import { ITenantScanBrowserTimeouts } from './tenant.interface';
import { ManualEvaluationSource } from '../constants/manual-audit';
import { $sortingOrder } from '../constants/sort';
import type { AiScanStatus } from './manual-audit.interface';

export enum ExcludePagesOperation {
  contains = 'contains',
  does_not_contain = 'does_not_contain',
  ends_with = 'ends_with',
  starts_with = 'starts_with',
}

export interface SkippedPageData {
  url: string;
  error: any;
  extras?: any;
}

export interface BrokenLinkData {
  [$brokenLinkData.url]: string;
  [$brokenLinkData.responseCode]: number;
}

export interface ProcessingUrlData {
  [$urlScan.url]: string;
  [$urlScan.crawlDeepLevel]: number;
  [$urlScan.scanJobs]: { _id: string; name: string; status: string; scanErrors: IGlobalError[] }[];
}

/**
 * Structured metadata a customer can attach to a scan submission.
 * Declared as an index signature (not `Record<string, string>`) so that
 * `typescript-json-schema` emits `additionalProperties: { type: "string" }`
 * instead of `additionalProperties: false`.
 */
export interface IScanMetadata {
  [key: string]: string;
}

export interface IAiTestingPageResult {
  pageId: string;
  status: AiScanStatus;
  error?: string;
}

export interface IScan {
  _id?: any;
  [$scan.auditsId]: string;
  [$scan.audits]?: IAudits;
  [$scan.authPassword]?: string;
  [$scan.authUser]?: string;
  [$scan.authType]?: scanAuthenticationType;
  [$scan.brokenLinks]?: BrokenLinkData[];
  [$scan.scannableDocuments]?: IDiscoveredScannableDocument[];
  [$scan.discoverScannableDocuments]?: boolean;
  [$scan.conformanceLevel]?: standardNameAndLevel;
  [$scan.contentType]: string;
  [$scan.context]?: string;
  [$scan.contextDescription]?: string;
  [$scan.crawled]: boolean;
  [$scan.crawlDeepLevel]: number | undefined;
  [$scan.createdAtTimezoneOffset]: number | string;
  [$scan.customFiles]: IFile[] | undefined;
  [$scan.customLinks]?: string[];
  [$scan.digitalProperty]: string | IDigitalProperty;
  [$scan.headers]: IScanHeader[] | undefined;
  [$scan.excludeUrls]?: string[];
  [$scan.excludeUrlOperations]?: ExcludePagesOperation[];
  [$scan.errorReason]?: string | undefined;
  [$scan.dlqError]?: IDlqError;
  [$scan.findingsArchival]?: scanFindingArchivalState | undefined;
  [$scan.findingsArchivalDate]?: Date | string | undefined;
  [$scan.includeUrls]: string[] | undefined;
  [$scan.languageForScanning]: string;
  [$scan.limitCrawlingPagesTo]: number | undefined;
  [$scan.manualUploadFiles]?: (string | IUploads)[];
  [$scan.manualAuditStatus]?: manualAuditStatus;
  [$scan.manualAuditLastUploadDate]?: Date;
  [$scan.manualAuditLastUploadedBy]?: string;
  [$scan.aiTestingPagesTotal]?: number;
  [$scan.aiTestingPageResults]?: IAiTestingPageResult[];
  [$scan.aiTestingStartedAt]?: Date;
  [$scan.pageTitle]: string;
  [$scan.processingStartedAt]: Date | string;
  [$scan.processingEndedAt]: Date | string;
  [$scan.scanTimeoutMinutes]?: number;
  [$scan.resourceType]: string;
  [$scan.scanningType]: scanningType;
  [$scan.sendEmail]: boolean;
  [$scan.scheduledAt]: string | Date;
  [$scan.scheduleId]: string | IScanSchedule;
  [$scan.secureWebAuthentication]?: ISecureWebAuthentication | undefined;
  [$scan.skipHashes]: boolean;
  [$scan.skipQueryParams]: boolean;
  [$scan.skippedPages]?: SkippedPageData[];
  [$scan.selectedAudits]?: AuditTypes[];
  [$scan.status]: scanStatus;
  [$scan.crawlingStatus]?: CrawlingStatus;
  [$scan.tag]?: IScanTag | string;
  [$scan.title]: string;
  [$scan.url]: string;
  [$scan.totalCrawledPages]: number;
  [$scan.totalScannedPages]: number;
  [$scan.urlResolved]: string | undefined;
  [$scan.user]: string | IUser;
  [$scan.previousScans]: string[] | IScan[];
  [$scan.pagesWithErrors]?: number;
  [$scan.workspace]: string | IWorkspace;
  [$scan.userAgent]: string;
  [$scan.viewport]?: IScanViewport;
  [$scan.isArchived]?: boolean;
  [$scan.archivedBy]?: IUser | string;
  [$scan.createdAt]: string;
  [$scan.updatedAt]: string;
  [$scan.scanOrigin]: ScanOrigin;
  [$scan.emailsToNotify]?: string[];
  [$scan.handlePopupsSelector]?: string;
  [$scan.includeIframes]?: boolean;
  [$scan.preScanFlowId]?: string;
  [$scan.tenantId]?: string;
  [$scan.waitTimeSec]?: number;
  [$scan.useScanningMicroservice]?: boolean;
  [$scan.isMobileFlawMatchingEnabled]?: boolean;
  [$scan.metadata]?: IScanMetadata;
}

export interface IScanWithScore extends IScan {
  [$scan.score]?: number;
}

export interface IAutomatedUserFlowError {
  [$automatedUserFlowError.actionId]: string;
  [$automatedUserFlowError.actionType]: UserFlowActionType;
  [$automatedUserFlowError.actionValue]: string;
  [$automatedUserFlowError.errorMessage]: string;
  [$automatedUserFlowError.stackTrace]?: string;
  [$automatedUserFlowError.url]?: string;
  [$automatedUserFlowError.screenshotPath]?: string;
}

export interface IAutomatedUserFlowDropdownError extends IAutomatedUserFlowError {
  [$automatedUserFlowError.actionCssSelector]: string;
}

export interface IAutomatedUserFlowChangeInputError extends IAutomatedUserFlowError {
  [$automatedUserFlowError.actionCssSelector]: string;
}

export interface IDlqError {
  [$dlqError.sourceTopic]: string;
  [$dlqError.errorMessage]: string;
  [$dlqError.stackTrace]?: string;
  [$dlqError.retryCount]?: number;
  [$dlqError.timestamp]: string;
  [$dlqError.url]?: string;
}

export interface IAutomatedUserFlowScan extends IScan {
  [$scan.scanningType]: scanningType.automatedUserFlow;
  [$scan.automatedUserFlowId]: string;
  [$scan.automatedUserFlowError]?: IAutomatedUserFlowError | IAutomatedUserFlowDropdownError;
}

export interface IManualScan extends IScan {
  [$scan.scanningType]: scanningType.manualAudit;
  [$scan.origin]: Origin;
  [$scan.scope]?: IScanScope;
  [$scan.source]?: ManualEvaluationSource;
  [$scan.originalScanId]?: string;
  [$scan.isGoldenEvaluation]?: boolean;
}

export interface IMobileManualScan extends IScan {
  [$scan.scanningType]: scanningType.mobileManualAudit;
  [$scan.scope]: {
    testGuideId: string;
    medium: EvaluationMediums.androidApp | EvaluationMediums.iOSApp;
    standards: EvaluationStandards[];
    userFlows: IMobileEvaluationUserFlows;
  };
  [$scan.origin]: Origin.staff;
  scopeApprovedAt?: Date;
  scopeApprovedBy?: string;
  /** Total time spent editing the scope; default 0. */
  scopingEditDurationMs: number;
}

export interface IMobileScan extends IScan {
  [$scan.mobile]: IMobileScanDetails;
}

export interface IMobileScanDetails {
  [$scanMobile.platformName]: $scanMobilePlatformName;
  [$scanMobile.platformVersion]: string;
  [$scanMobile.deviceManufacturer]?: string;
  [$scanMobile.deviceModel]: string;
  [$scanMobile.deviceName]: string;
  [$scanMobile.deviceType]: $scanMobileDeviceType;
  [$scanMobile.appIdentifier]?: string;
  [$scanMobile.appVersion]?: string;
  [$scanMobile.continuumSdkVersion]?: string;
  [$scanMobile.appiumAutomationName]?: string;

  // may be null for old entries, referencing FK in device-api's AppFile collection for LAT scans
  [$scanMobile.appFileId]?: string;
}

export interface IMobileScanDetailsForXMLScan {
  [$scanMobile.platformName]: $scanMobilePlatformName;
  [$scanMobile.platformVersion]: string;
  [$scanMobile.deviceManufacturer]?: string;
  [$scanMobile.deviceModel]: string;
  [$scanMobile.deviceName]: string;
  [$scanMobile.deviceType]: $scanMobileDeviceType;
  [$scanMobile.appIdentifier]: string;
  [$scanMobile.appVersion]?: string;
  [$scanMobile.appiumAutomationName]?: string;
}

export type IMobileScanOperatingSystem = Pick<IMobileScanDetails, $scanMobile.platformName | $scanMobile.platformVersion>;

export interface IManualScanEdit extends IManualScan {
  testingStarted: boolean;
  latestScopingCrawl?: {
    _id: string;
    status: ScopingCrawlStatus;
    updatedAt: Date;
    url: string;
    crawlDeepLevel: number;
    limitCrawlingPagesTo: number;
    skipHashes: boolean;
    skipQueryParams: boolean;
    excludeUrls: string[];
  };
  /** Only for an evaluation in scoping status. */
  recommendedPages?: IScopingRecommendedPage[];
}

export interface IGetScansServerResponse {
  _total: number;
  scans: IScanWithScore[];
}

export interface IScreenshotDetail {
  title: string;
  base64Data: string;
  filter: string;
}

export interface IActiveScan {
  _id: string;
  [$scan.user]: string;
  [$scan.audits]?: IAudits;
  [$scan.status]: scanStatus;
  [$scan.totalScannedPages]: number;
  [$scan.totalCrawledPages]: number;
  [$scan.limitCrawlingPagesTo]?: number;
  [$scan.title]: string;
  [$scan.urlResolved]: string;
  [$scan.processingStartedAt]: string;
  [$scan.scanningType]: scanningType;
  [$scan.workspace]: string | IWorkspace;
  [$scan.digitalProperty]: string;
  [$scan.selectedAudits]: string[];
  [$scan.viewport]?: IScanViewport;
}

export type GetScansScanningType = Exclude<scanningType, scanningType.manualAudit>;

export type GetScansScanningTypeQuery = GetScansScanningType | GetScansScanningType[];
export type ScanStatusQuery = scanStatus | scanStatus[];
export type GetScansTagQuery = IdString | IdStringArray;

export interface IGetScansQuery {
  [$scanSearch.createdAt]?: string;
  [$scanSearch.entriesAmount]?: string;
  [$scanSearch.limit]?: string;
  [$scanSearch.scanningType]?: GetScansScanningTypeQuery;
  [$scanSearch.scanOrigin]?: ScanOrigin;
  [$scanSearch.searchTerm]?: string;
  [$scanSearch.skip]?: string;
  [$scanSearch.page]?: string;
  [$scanSearch.status]?: ScanStatusQuery;
  [$scanSearch.url]?: string;
  [$scanSearch.tag]?: GetScansTagQuery;
  [$scanSearch.title]?: string;
  [$scanSearch.tool]?: AccessibilityAuditToolNames;
  [$scanSearch.mobileAppVersion]?: string;
  [$scanSearch.mobilePlatformName]?: string;
  [$scanSearch.mobilePlatformVersion]?: string;

  [ApiQueryOption.sortBy]?: string;
  [ApiQueryOption.direction]?: $sortingOrder.asc | $sortingOrder.desc;
}

export interface ISendEmailsRequest {
  /**
   * @items.type string
   * @items.format email
   */
  [$scan.emails]: string[];
  auditTool?: AccessibilityAuditToolNames;
}

export interface IQuickScanOptions {
  [$scanAutomatedOptions.scanningType]: scanningType;
  [$scanAutomatedOptions.title]?: string;
  [$scanAutomatedOptions.viewport]: IScanViewport;
  [$scanAutomatedOptions.conformanceLevel]: standardNameAndLevel;
  [$scanAutomatedOptions.url]: string;
  [$scanAutomatedOptions.tag]: string;
}

export interface IRerunScanForPagesOptions {
  [$rerunScan.pages]: string[];
}

export interface ISupplementalScanOption {
  [$scan.url]: string;
}

export interface IScanHeader {
  [$scanHeaders.name]: string;
  [$scanHeaders.value]: string;
}

export interface IScanOptions {
  [$scanOptions.browserFlags]: string[];
  [$scanOptions.browserTimeouts]: ITenantScanBrowserTimeouts;
}

export interface IUpdateScanTitleBody {
  [$scan._id]: IdString;
  [$scan.title]: string;
}

export interface IUpdateScanStandardsBody {
  [$scanScope.standards]: EvaluationStandards[];
  [$scan.conformanceLevel]?: standardNameAndLevel;
}

export interface IScanScope {
  [$scanScope.testGuideId]: string;
  [$scanScope.medium]: EvaluationMediums;
  [$scanScope.standards]: EvaluationStandards[];
  [$scanScope.testGuide]?: ITestGuide;
  [$scanScope.pages]: IPageWithComponents[];
}

export enum Origin {
  staff = 'staff',
  client = 'client',
}

export interface IPageWithComponents {
  [$pageWithComponents.pageId]: string;
  [$pageWithComponents.page]?: IPage;
  [$pageWithComponents.componentIds]: string[];
  [$pageWithComponents.applySwa]?: boolean;
  [$pageWithComponents.components]?: IMatchComponent[];
}
